import { gql } from "@apollo/client";
import type { SyncableClinicalProductForm } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ClinicalProductForm as RxClinicalProductForm } from "../collections/clinical-product-form";
import type { RxDBCollections } from "../provider";
import {
  SyncClinicalProductFormPullDocument,
  type WholeSyncableClinicalProductFormFragment,
} from "./__generated__/clinical-product-form.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateClinicalProductForm = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxClinicalProductForm>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncClinicalProductFormPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<
    RxClinicalProductForm,
    SyncableClinicalProductForm,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlClinicalProductFormToRxClinicalProductForm,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "clinical-product-form",
  });
};

const mapGqlClinicalProductFormToRxClinicalProductForm = (
  clinicalProductForm: WholeSyncableClinicalProductFormFragment,
): WithDeleted<RxClinicalProductForm> => ({
  id: clinicalProductForm.id,
  title: {
    en: clinicalProductForm.title.en,
    es: clinicalProductForm.title.es ?? null,
    fr: clinicalProductForm.title.fr ?? null,
  },
  keywords: [...clinicalProductForm.keywords],
  snomedId: clinicalProductForm.snomedId,
  meta: {
    createdAt: clinicalProductForm.meta.createdAt,
    createdByUserId: clinicalProductForm.meta.createdByUserId,
    updatedAt: clinicalProductForm.meta.updatedAt,
    updatedByUserId: clinicalProductForm.meta.updatedByUserId,
  },
  _deleted: clinicalProductForm.meta.deleted,
});

gql`
  fragment WholeSyncableClinicalProductForm on SyncableClinicalProductForm {
    id
    title {
      en
      es
      fr
    }
    keywords
    snomedId
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncClinicalProductFormPull($input: PullClinicalProductFormInput!) {
    pullClinicalProductForm(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableClinicalProductForm
      }
    }
  }
`;
